import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, type MessageAuthor, type Poll, type Uuid } from "@shared/api";
import { plural } from "@shared/lib/format";
import { Button } from "@shared/ui/button";
import { Dialog } from "@shared/ui/bits";
import { Spinner } from "@shared/ui/spinner";
import { fromAuthor, Tile } from "@entities/user";

/** Сколько осталось — крупно: «3 ч», «20 мин», «2 дн». */
function left(expires: string, now: number): string {
  const minutes = Math.max(1, Math.round((new Date(expires).getTime() - now) / 60_000));
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} ч` : `${Math.round(hours / 24)} дн`;
}

/**
 * Опрос в ленте — как в Discord. Пока не голосовали и опрос идёт — ответы
 * нажимаются: один ответ уходит сразу, несколько — кнопкой «Голосовать».
 * Проголосовали или опрос закончился — видны доли, свой выбор отмечен, а голос
 * можно снять, пока опрос идёт. Как в Discord, результаты можно посмотреть и
 * не голосуя, нажатие на ответ показывает, кто за него, а автор (или тот, кто
 * управляет сообщениями) может закончить опрос досрочно.
 */
export function PollCard({
  poll,
  onVote,
  onEnd,
  voters,
}: {
  poll: Poll;
  onVote?: (answerIds: Uuid[]) => Promise<void>;
  onEnd?: () => Promise<void>;
  voters: (answerId: Uuid) => Promise<MessageAuthor[]>;
}) {
  const [picked, setPicked] = useState<Uuid[]>([]);
  const [peek, setPeek] = useState(false);
  const [who, setWho] = useState<{ id: Uuid; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /*
   * «Сейчас» снимается при показе и заново, когда у опроса сменился срок: его
   * закончили досрочно, и новый срок позже той, первой отметки.
   */
  const [seenExpires, setSeenExpires] = useState(poll.expires);
  if (seenExpires !== poll.expires) {
    setSeenExpires(poll.expires);
    setNow(Math.max(Date.parse(poll.expires), now));
  }

  const over = new Date(poll.expires).getTime() <= now;
  const voted = poll.mine.length > 0;
  const results = over || voted || !onVote || peek;
  const total = poll.answers.reduce((sum, answer) => sum + answer.votes, 0);

  async function end() {
    if (!onEnd) return;
    setBusy(true);
    setProblem(null);
    try {
      await onEnd();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не вышло закончить опрос");
    } finally {
      setBusy(false);
    }
  }

  async function vote(ids: Uuid[]) {
    if (!onVote) return;
    setBusy(true);
    setProblem(null);
    try {
      await onVote(ids);
      setPicked([]);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Голос не ушёл");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="poll">
      <div className="poll-question">{poll.question}</div>
      <div className="tiny poll-hint">
        {poll.multiselect ? "Выберите один или несколько ответов" : "Выберите один ответ"}
      </div>

      <div className="poll-answers">
        {poll.answers.map((answer) => {
          const share = total ? Math.round((answer.votes / total) * 100) : 0;
          const mine = poll.mine.includes(answer.id);
          const on = picked.includes(answer.id);
          return results ? (
            <button
              key={answer.id}
              type="button"
              className={`poll-answer result ${mine ? "mine" : ""}`}
              title={answer.votes > 0 ? "Кто проголосовал" : undefined}
              disabled={answer.votes === 0}
              onClick={() => setWho({ id: answer.id, text: answer.text })}
            >
              <span className="poll-bar" style={{ width: `${share}%` }} />
              <span className="grow ellipsis">{answer.text}</span>
              <span className="tiny">
                {answer.votes} · {share}%
              </span>
            </button>
          ) : (
            <button
              key={answer.id}
              type="button"
              className={`poll-answer ${on ? "on" : ""}`}
              disabled={busy}
              onClick={() => {
                if (!poll.multiselect) void vote([answer.id]);
                else setPicked((previous) => (on ? previous.filter((id) => id !== answer.id) : [...previous, answer.id]));
              }}
            >
              {poll.multiselect && <span className="poll-check">{on ? "✓" : ""}</span>}
              <span className="grow ellipsis">{answer.text}</span>
            </button>
          );
        })}
      </div>

      <div className="line tiny poll-foot" style={{ gap: 8 }}>
        <span className="grow">
          {total} {plural(total, "голос", "голоса", "голосов")} · {over ? "опрос завершён" : `осталось ${left(poll.expires, now)}`}
        </span>
        {!results && poll.multiselect && (
          <Button type="button" size="sm" disabled={busy || picked.length === 0} onClick={() => void vote(picked)}>
            Голосовать
          </Button>
        )}
        {voted && !over && onVote && (
          <button type="button" className="set-link" disabled={busy} onClick={() => void vote([])}>
            Убрать голос
          </button>
        )}
        {!voted && !over && onVote && (
          <button type="button" className="set-link" onClick={() => setPeek((value) => !value)}>
            {peek ? "К голосованию" : "Показать результаты"}
          </button>
        )}
        {!over && onEnd && (
          <button type="button" className="set-link" disabled={busy} onClick={() => void end()}>
            Завершить опрос
          </button>
        )}
      </div>

      {problem && <div className="tiny danger-text">{problem}</div>}
      {who && <Voters answer={who} voters={voters} onClose={() => setWho(null)} />}
    </div>
  );
}

/** Кто проголосовал за ответ — как в Discord. */
function Voters({
  answer,
  voters,
  onClose,
}: {
  answer: { id: Uuid; text: string };
  voters: (answerId: Uuid) => Promise<MessageAuthor[]>;
  onClose: () => void;
}) {
  const { data: people, isPending } = useQuery({
    queryKey: ["poll-voters", answer.id],
    queryFn: () => voters(answer.id),
    staleTime: 10_000,
  });
  return (
    <Dialog title={`«${answer.text}»`} onClose={onClose}>
      <div className="reactions-dialog-people">
        {isPending ? (
          <Spinner />
        ) : (
          (people ?? []).map((person) => (
            <div key={person.id} className="reactions-dialog-person">
              <Tile person={fromAuthor(person)} size={26} mark={null} />
              <span className="ellipsis">{person.display_name || person.username}</span>
              <span className="tiny muted ellipsis">{person.handle}</span>
            </div>
          ))
        )}
      </div>
    </Dialog>
  );
}
